import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatNaira } from "@/lib/mock-data";
import { walletApi, type WalletTransaction } from "@/lib/api";
import { Download, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { LoadingBlock } from "@/components/ui/spinner";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/stores/auth";

function formatKobo(amountKobo: number) {
  return formatNaira(amountKobo / 100);
}

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

const filters = [
  { v: "all", l: "All" },
  { v: "topup", l: "Top-ups" },
  { v: "payout", l: "Payouts" },
  { v: "delivery_fee", l: "Delivery fees" },
  { v: "refund", l: "Refunds" },
];

export default function Transactions() {
  const token = useAuth((s) => s.session?.accessToken);
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const txns = await walletApi.transactions(undefined, token);
        if (!cancelled) setTransactions(txns);
      } catch {
        if (!cancelled) toast.error("Failed to load transactions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = transactions.filter((t) => {
    if (f !== "all" && t.type !== f) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return t.reference.toLowerCase().includes(s) || (t.method ?? "").toLowerCase().includes(s);
  });

  return (
    <>
      <Topbar
        title="Transactions"
        subtitle={`${transactions.length} entries`}
        actions={<Button variant="outline" size="sm"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>}
      />
      <div className="flex-1 p-4 md:p-6">
        <div className="surface-card">
          <div className="space-y-3 border-b p-4">
            <div className="relative max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference or method…" className="pl-9" aria-label="Search transactions" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {filters.map((x) => (
                <button
                  key={x.v}
                  onClick={() => setF(x.v)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    f === x.v ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"
                  )}
                >
                  {x.l}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Reference</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Provider</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7}><LoadingBlock label="Loading transactions…" /></td></tr>
                ) : (
                  <>
                    {rows.map((t) => (
                      <tr key={t.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs font-semibold">{t.reference}</td>
                        <td className="whitespace-nowrap px-4 py-3 capitalize">{t.type.replace("_", " ")}</td>
                        <td className="whitespace-nowrap px-4 py-3 capitalize">{t.provider}</td>
                        <td className="whitespace-nowrap px-4 py-3">{t.method ?? "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{formatDateTime(t.occurred_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <span
                            className={cn(
                              "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
                              t.status === "success" && "bg-success/10 text-success border-success/20",
                              t.status === "pending" && "bg-warning/15 text-warning-foreground border-warning/30",
                              t.status === "failed" && "bg-destructive/10 text-destructive border-destructive/20"
                            )}
                          >
                            {t.status}
                          </span>
                        </td>
                        <td className={cn("whitespace-nowrap px-4 py-3 text-right font-semibold", t.amount_kobo >= 0 ? "text-success" : "")}>
                          {formatKobo(t.amount_kobo)}
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 ? (
                      <tr><td colSpan={7} className="p-10 text-center text-sm text-muted-foreground">No transactions match your filters.</td></tr>
                    ) : null}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
